/**
 * The read layer for the money screens.
 *
 * Everything is read from the LOCAL Dexie mirror, never from the network
 * (docs/02 §8). The mirror stores server rows in snake_case; the domain speaks
 * camelCase branded types. This file is the single crossing point between the
 * two, so no screen ever handles a raw row.
 *
 * **The outbox overlay.** `mutations.payBill`, `mutations.addExpense` and
 * `mutations.settle` enqueue without writing the mirror, so a payment recorded
 * offline would otherwise vanish from the screen until the next sync — on a
 * phone that is indistinguishable from having lost it. We therefore project the
 * money mutations still sitting in the outbox back into the snapshot, and merge
 * them by id. That merge is exact rather than approximate: the server inserts
 * each of these rows with the MUTATION's own id as the primary key
 * (migrations/004 `VALUES (m_id, ...)`), so when the real row syncs down it
 * replaces its own optimistic copy and nothing is ever counted twice.
 */

import { useEffect, useState } from 'react'
import { liveQuery } from 'dexie'
import {
  type Bill,
  type BillCycle,
  type BillId,
  type BillInstance,
  type BillInstanceId,
  type BillPayment,
  type Expense,
  type ExpenseSource,
  type ExpenseSplit,
  type MemberId,
  type PaymentId,
  type PaymentMethod,
  type PersonId,
  type Piastres,
  type Settlement,
  type SettlementId,
  type SplitKind,
  type DayKey,
  type ExpenseId,
  asId,
  piastres,
} from '@office/domain'
import type { OfficeDb } from '../../lib/db.js'

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export interface MoneyPerson {
  id: PersonId
  kind: 'member' | 'guest'
  displayName: string
}

// ---------------------------------------------------------------------------
// The snapshot every money screen renders from
// ---------------------------------------------------------------------------

export interface MoneySnapshot {
  /**
   * Everyone the mirror knows, ARCHIVED ROWS INCLUDED. Nothing is picked from
   * this list — it exists so the two questions that are about a person rather
   * than about the roster can still be answered after they leave it: is this
   * person a guest, and what is their name.
   */
  allPeople: MoneyPerson[]
  /** Everyone who can appear in a NEW split: owners and guests, unarchived. */
  people: MoneyPerson[]
  /** Owners only. The only people who can carry a balance (docs/01 §7.2). */
  members: MoneyPerson[]
  /** Guests. They split the arithmetic and then disappear from the ledger. */
  guests: MoneyPerson[]
  bills: Bill[]
  instances: BillInstance[]
  payments: BillPayment[]
  expenses: Expense[]
  settlements: Settlement[]
  /** Records queued in the outbox and not yet acknowledged by the server. */
  pendingIds: ReadonlySet<string>
  /**
   * The last read of the local mirror failed. A ledger that cannot be read must
   * never render as an empty one: "all square" and "we could not tell" look
   * identical on screen and only one of them is safe to act on.
   */
  readFailed: boolean
}

export const EMPTY_SNAPSHOT: MoneySnapshot = {
  allPeople: [],
  people: [],
  members: [],
  guests: [],
  bills: [],
  instances: [],
  payments: [],
  expenses: [],
  settlements: [],
  pendingIds: new Set<string>(),
  readFailed: false,
}

// ---------------------------------------------------------------------------
// Row coercion — defensive, because a mirror replays whatever synced
// ---------------------------------------------------------------------------

type Row = Record<string, unknown>

const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback)

/** Postgres `bigint` arrives as a string over JSON; `numeric` may too. */
function amount(v: unknown): Piastres {
  const n = typeof v === 'number' ? v : Number(v)
  return piastres(Number.isFinite(n) ? n : 0)
}

/** A stored instant is an ISO string; the trusted clock's own value is a number. */
function instant(v: unknown): number {
  if (typeof v === 'number') return v
  const parsed = Date.parse(str(v))
  return Number.isFinite(parsed) ? parsed : 0
}

/** A `date` column may arrive as `YYYY-MM-DD` or as a full ISO timestamp. */
const dayKey = (v: unknown): DayKey => str(v).slice(0, 10) as DayKey

const CYCLES: readonly BillCycle[] = ['monthly', 'quarterly', 'yearly', 'once']
const METHODS: readonly PaymentMethod[] = ['cash', 'transfer', 'card', 'other']
const SPLIT_KINDS: readonly SplitKind[] = ['equal', 'exact', 'shares']
const SOURCES: readonly ExpenseSource[] = ['manual', 'bill_payment', 'food_order']

const oneOf = <T extends string>(options: readonly T[], v: unknown, fallback: T): T =>
  options.includes(v as T) ? (v as T) : fallback

function toSplits(v: unknown): ExpenseSplit[] {
  if (!Array.isArray(v)) return []
  return v.map(raw => {
    const s = (raw ?? {}) as Row
    return {
      personId: asId<PersonId>(str(s['personId'])),
      shareAmount: amount(s['shareAmount']),
      settledImmediately: s['settledImmediately'] === true,
    }
  })
}

export function toBill(r: Row): Bill {
  return {
    id: asId<BillId>(str(r['id'])),
    name: str(r['name']),
    category: str(r['category'], 'utilities'),
    amountExpected: amount(r['amount_expected']),
    cycle: oneOf(CYCLES, r['cycle'], 'monthly'),
    dueDayOfMonth: Number(r['due_day_of_month']) || 1,
    active: r['active'] !== false,
    defaultPayerId: r['default_payer_id'] == null ? null : asId<MemberId>(str(r['default_payer_id'])),
    splitAmong: Array.isArray(r['split_among'])
      ? (r['split_among'] as unknown[]).map(m => asId<MemberId>(str(m)))
      : [],
    createdAt: instant(r['created_at']),
  }
}

export function toBillInstance(r: Row): BillInstance {
  return {
    id: asId<BillInstanceId>(str(r['id'])),
    billId: asId<BillId>(str(r['bill_id'])),
    periodLabel: str(r['period_label']),
    dueDay: dayKey(r['due_day']),
    amountDue: amount(r['amount_due']),
  }
}

export function toBillPayment(r: Row): BillPayment {
  return {
    id: asId<PaymentId>(str(r['id'])),
    billInstanceId: asId<BillInstanceId>(str(r['bill_instance_id'])),
    amount: amount(r['amount']),
    paidAt: instant(r['paid_at']),
    paidByMemberId: asId<MemberId>(str(r['paid_by_member_id'])),
    method: oneOf(METHODS, r['method'], 'cash'),
    note: r['note'] == null ? null : str(r['note']),
    receiptRef: r['receipt_ref'] == null ? null : str(r['receipt_ref']),
  }
}

export function toExpense(r: Row): Expense {
  return {
    id: asId<ExpenseId>(str(r['id'])),
    description: str(r['description']),
    amount: amount(r['amount']),
    spentAt: instant(r['spent_at']),
    paidByMemberId: asId<MemberId>(str(r['paid_by_member_id'])),
    category: str(r['category'], 'general'),
    splitKind: oneOf(SPLIT_KINDS, r['split_kind'], 'equal'),
    splits: toSplits(r['splits']),
    source: oneOf(SOURCES, r['source'], 'manual'),
    sourceRef: r['source_ref'] == null ? null : str(r['source_ref']),
    createdAt: instant(r['created_at']),
  }
}

export function toSettlement(r: Row): Settlement {
  return {
    id: asId<SettlementId>(str(r['id'])),
    fromMemberId: asId<MemberId>(str(r['from_member_id'])),
    toMemberId: asId<MemberId>(str(r['to_member_id'])),
    amount: amount(r['amount']),
    settledAt: instant(r['settled_at']),
    method: oneOf(METHODS, r['method'], 'cash'),
    note: r['note'] == null ? null : str(r['note']),
  }
}

// ---------------------------------------------------------------------------
// Outbox projection
// ---------------------------------------------------------------------------

export interface OutboxRow {
  id: string
  kind: string
  payload: Record<string, unknown>
}

export interface PendingMoney {
  payments: BillPayment[]
  expenses: Expense[]
  settlements: Settlement[]
  ids: Set<string>
}

/**
 * Project queued money mutations into domain records so the screen shows what
 * the user just did, offline, before any server has seen it.
 */
export function pendingFromOutbox(rows: readonly OutboxRow[]): PendingMoney {
  const out: PendingMoney = { payments: [], expenses: [], settlements: [], ids: new Set() }
  for (const row of rows) {
    const d = row.payload ?? {}
    if (row.kind === 'bill.payment') {
      out.payments.push({
        id: asId<PaymentId>(row.id),
        billInstanceId: asId<BillInstanceId>(str(d['billInstanceId'])),
        amount: amount(d['amount']),
        paidAt: instant(d['paidAt']),
        paidByMemberId: asId<MemberId>(str(d['paidBy'])),
        method: oneOf(METHODS, d['method'], 'cash'),
        note: d['note'] == null ? null : str(d['note']),
        receiptRef: null,
      })
      out.ids.add(row.id)
    } else if (row.kind === 'expense.create') {
      out.expenses.push({
        id: asId<ExpenseId>(row.id),
        description: str(d['description']),
        amount: amount(d['amount']),
        spentAt: instant(d['spentAt']),
        paidByMemberId: asId<MemberId>(str(d['paidBy'])),
        category: str(d['category'], 'general'),
        splitKind: oneOf(SPLIT_KINDS, d['splitKind'], 'equal'),
        splits: toSplits(d['splits']),
        source: oneOf(SOURCES, d['source'], 'manual'),
        sourceRef: d['sourceRef'] ? str(d['sourceRef']) : null,
        createdAt: instant(d['createdAt']),
      })
      out.ids.add(row.id)
    } else if (row.kind === 'settlement.create') {
      out.settlements.push({
        id: asId<SettlementId>(row.id),
        fromMemberId: asId<MemberId>(str(d['from'])),
        toMemberId: asId<MemberId>(str(d['to'])),
        amount: amount(d['amount']),
        settledAt: instant(d['at']),
        method: oneOf(METHODS, d['method'], 'cash'),
        note: d['note'] == null ? null : str(d['note']),
      })
      out.ids.add(row.id)
    }
  }
  return out
}

/** Mirror rows win; a queued mutation only shows until its own row syncs back. */
function mergeById<T extends { id: string }>(synced: T[], pending: T[]): T[] {
  const seen = new Set(synced.map(x => x.id))
  return [...synced, ...pending.filter(p => !seen.has(p.id))]
}

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

export async function readMoney(db: OfficeDb): Promise<MoneySnapshot> {
  const [peopleRows, billRows, instanceRows, paymentRows, expenseRows, settlementRows, outboxRows] =
    await Promise.all([
      db.people.toArray(),
      db.bills.toArray(),
      db.bill_instances.toArray(),
      db.bill_payments.toArray(),
      db.expenses.toArray(),
      db.settlements.toArray(),
      db.outbox.toArray(),
    ])

  const allPeople: MoneyPerson[] = peopleRows
    .map(r => ({
      id: asId<PersonId>(str(r['id'])),
      kind: str(r['kind']) === 'guest' ? ('guest' as const) : ('member' as const),
      displayName: str(r['display_name']),
      archived: r['archived_at'] != null,
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .map(({ id, kind, displayName }) => ({ id, kind, displayName }))

  // Archived rows are dropped from everything that gets PICKED from — you
  // cannot add a former owner to a new split — but never from `allPeople`.
  const archivedIds = new Set<string>(
    peopleRows.filter(r => r['archived_at'] != null).map(r => str(r['id'])),
  )
  const people = allPeople.filter(p => !archivedIds.has(p.id))

  const pending = pendingFromOutbox(
    outboxRows.map(m => ({ id: m.id, kind: m.kind, payload: m.payload })),
  )

  return {
    allPeople,
    people,
    members: people.filter(p => p.kind === 'member'),
    guests: people.filter(p => p.kind === 'guest'),
    bills: billRows.map(toBill).sort((a, b) => a.name.localeCompare(b.name)),
    instances: instanceRows.map(toBillInstance),
    payments: mergeById(paymentRows.map(toBillPayment), pending.payments),
    expenses: mergeById(expenseRows.map(toExpense), pending.expenses),
    settlements: mergeById(settlementRows.map(toSettlement), pending.settlements),
    pendingIds: pending.ids,
    readFailed: false,
  }
}

/**
 * Live snapshot of the money tables. Dexie's own `liveQuery` re-runs the read
 * whenever any table it touched is written, including the outbox — so
 * recording a payment updates the ledger on the next tick with no plumbing.
 */
export function useMoneySnapshot(db: OfficeDb): MoneySnapshot {
  const [snapshot, setSnapshot] = useState<MoneySnapshot>(EMPTY_SNAPSHOT)
  useEffect(() => {
    const sub = liveQuery(() => readMoney(db)).subscribe({
      next: value => setSnapshot(value),
      // Keep whatever we last knew and flag it, rather than blanking the screen:
      // a failed read that renders as "nobody owes anybody anything" is a wrong
      // answer wearing the clothes of a right one (docs/02 §8).
      error: () => setSnapshot(prev => ({ ...prev, readFailed: true })),
    })
    return () => { sub.unsubscribe() }
  }, [db])
  return snapshot
}

// ---------------------------------------------------------------------------
// Snapshot helpers
// ---------------------------------------------------------------------------

/**
 * The guard the ledger is handed. Anyone the mirror knows to be a guest is
 * excluded from every balance, on every side of every row (docs/01 §7.2).
 * A person the mirror has never heard of is NOT assumed to be a member.
 *
 * It reads `allPeople`, not `guests`, and that is the whole point: guests get
 * ARCHIVED once they stop visiting, and a predicate built from the live roster
 * would quietly stop recognising them. Every split, expense and settlement that
 * named them is still in the mirror, so the guest would reappear in `pairs` and
 * `net` — carrying an ongoing balance, under a truncated uuid, for good.
 */
export function guestPredicate(snapshot: MoneySnapshot): (p: PersonId) => boolean {
  const guests = new Set<string>(snapshot.guests.map(g => g.id))
  for (const p of snapshot.allPeople) if (p.kind === 'guest') guests.add(p.id)
  return (p: PersonId) => guests.has(p)
}

/**
 * A person's name, or a short form of their id when the mirror lacks the row.
 * Archived people are named too: history keeps naming them long after they come
 * off the roster.
 */
export function nameOf(snapshot: MoneySnapshot, id: PersonId | MemberId): string {
  const found =
    snapshot.allPeople.find(p => p.id === (id as string)) ??
    snapshot.people.find(p => p.id === (id as string))
  return found ? found.displayName : `${String(id).slice(0, 8)}…`
}

export const billById = (snapshot: MoneySnapshot, id: BillId): Bill | undefined =>
  snapshot.bills.find(b => b.id === id)

export const instanceById = (
  snapshot: MoneySnapshot,
  id: BillInstanceId,
): BillInstance | undefined => snapshot.instances.find(i => i.id === id)
